import type { CustomerPortalSection, PlanSelection, TravelFormData, ClientInformationData } from '../types/customer-portal';

// Session state interface
interface SessionState {
  activeSection: CustomerPortalSection;
  planSelection?: PlanSelection;
  travelFormData?: TravelFormData;
  clientInformationData?: ClientInformationData;
  verificationEmail?: string;
  stepperState?: {
    currentStep: string;
    stepperData: Record<string, unknown>;
    planType: string;
    coverageType: string;
    showStepper: boolean;
    selectedOption: string;
  };
  timestamp: string;
  // Enhanced stepper state management
  stepperStateVersion?: string;
  lastStepperUpdate?: string;
}

// Storage keys
export const STORAGE_KEYS = {
  SESSION_STATE: 'customerPortalSessionState',
  TRAVEL_FORM_DATA: 'travelFormData',
  PLAN_SELECTION: 'planSelection',
  CLIENT_INFO: 'clientInformationData',
  ACTIVE_SECTION: 'activeSection',
  VERIFICATION_EMAIL: 'verificationEmail'
} as const;

/**
 * Session State Manager for Customer Portal
 * Handles data persistence and restoration across page refreshes
 */
export class SessionStateManager {
  private static instance: SessionStateManager;
  
  private constructor() {}
  
  static getInstance(): SessionStateManager {
    if (!SessionStateManager.instance) {
      SessionStateManager.instance = new SessionStateManager();
    }
    return SessionStateManager.instance;
  }

  /**
   * Save complete session state
   */
  saveSessionState(state: Partial<SessionState>): void {
    try {
      const currentState = this.getSessionState();
      const updatedState: SessionState = {
        ...currentState,
        ...state,
        timestamp: new Date().toISOString()
      };
      
      // Save to sessionStorage as primary, localStorage as backup for critical data only
      sessionStorage.setItem(STORAGE_KEYS.SESSION_STATE, JSON.stringify(updatedState));
      
      // Only save to localStorage for email verification and stepper state (critical data)
      if (state.verificationEmail || state.stepperState) {
        localStorage.setItem(STORAGE_KEYS.SESSION_STATE, JSON.stringify(updatedState));
      }
      
      console.log('SessionStateManager: Saved session state:', updatedState);
    } catch (error) {
      console.error('SessionStateManager: Error saving session state:', error);
    }
  }

  /**
   * Get complete session state
   */
  getSessionState(): SessionState {
    try {
      // Try sessionStorage first, then localStorage for fallback
      const sessionState = sessionStorage.getItem(STORAGE_KEYS.SESSION_STATE);
      const localState = localStorage.getItem(STORAGE_KEYS.SESSION_STATE);
      
      const stateData = sessionState || localState;
      
      if (stateData) {
        const parsed = JSON.parse(stateData);
        console.log('SessionStateManager: Retrieved session state:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('SessionStateManager: Error retrieving session state:', error);
    }
    
    // Return default state
    return {
      activeSection: 'landing',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Detect current section from URL path
   */
  detectSectionFromUrl(): CustomerPortalSection {
    const currentPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    
    console.log('SessionStateManager: Detecting section from URL:', { currentPath, search: window.location.search });
    
    // Handle specific URL patterns - order matters for specificity
    if (currentPath.includes('/payment-result/success')) return 'payment-success';
    if (currentPath.includes('/payment-result/failure')) return 'payment-failure';
    if (currentPath.includes('/email/verified')) return 'email-verified';
    if (currentPath.includes('/terms-conditions')) return 'terms-and-conditions';
    if (currentPath.includes('/privacy-statement')) return 'privacy-statement';
    if (currentPath.includes('/transaction/validation-success')) return 'policy-verification-success';
    if (currentPath.includes('/transaction/validation-invalid')) return 'policy-verification-invalid';
    if (currentPath.includes('/client-information')) return 'client-information';
    
    // Check for email verification parameters
    if (urlParams.has('id') && urlParams.has('token')) {
      return 'client-information'; // Email verification leads to client info
    }
    
    // ENHANCED: Check if this is a fresh tab (no sessionStorage data)
    if (this.isFreshTab()) {
      console.log('SessionStateManager: Fresh tab detected, defaulting to landing');
      return 'landing';
    }
    
    // ENHANCED: Check for travel flow sections based on stored session state
    const sessionState = this.getSessionState();
    
    // CRITICAL FIX: Check for data age and clear if too old (more than 1 hour)
    if (sessionState.timestamp) {
      const sessionTime = new Date(sessionState.timestamp);
      const now = new Date();
      const hoursDiff = (now.getTime() - sessionTime.getTime()) / (1000 * 60 * 60);
      
      if (hoursDiff > 1) {
        console.log('SessionStateManager: Session data too old, clearing and defaulting to landing');
        this.clearSessionState();
        return 'landing';
      }
    }
    
    // If we have valid travel data in session, we can restore the appropriate section
    if (sessionState.activeSection && sessionState.activeSection !== 'landing') {
      // Validate that the section makes sense with current data
      switch (sessionState.activeSection) {
        case 'travel-information':
          // Can always restore travel information if we have plan selection
          if (sessionState.planSelection) {
            console.log('SessionStateManager: Restoring travel-information section');
            return 'travel-information';
          }
          break;
          
        case 'travel-details':
          // Can restore travel details if we have both plan selection and travel form data
          if (sessionState.planSelection && sessionState.travelFormData) {
            console.log('SessionStateManager: Restoring travel-details section');
            return 'travel-details';
          }
          break;
          
        case 'email-validation':
          // Can restore email validation if we have travel data
          if (sessionState.planSelection && sessionState.travelFormData) {
            console.log('SessionStateManager: Restoring email-validation section');
            return 'email-validation';
          }
          break;
          
        case 'email-sent-confirmation':
          // Can restore email sent confirmation if we have verification email
          if (sessionState.verificationEmail && sessionState.travelFormData) {
            console.log('SessionStateManager: Restoring email-sent-confirmation section');
            return 'email-sent-confirmation';
          }
          break;
          
        case 'client-information':
          // Client information can be restored if we have travel data
          if (sessionState.planSelection && sessionState.travelFormData) {
            console.log('SessionStateManager: Restoring client-information section');
            return 'client-information';
          }
          break;
      }
    }
    
    // If no valid section can be restored, default to landing
    console.log('SessionStateManager: No valid section found, defaulting to landing');
    return 'landing';
  }

  /**
   * Check if we should restore session data based on current context
   */
  shouldRestoreSession(): boolean {
    const currentPath = window.location.pathname;
    const sessionState = this.getSessionState();
    
    // Don't restore for specific terminal pages
    if (currentPath.includes('/payment-result/') ||
        currentPath.includes('/email/verified') ||
        currentPath.includes('/terms-conditions') ||
        currentPath.includes('/privacy-statement') ||
        currentPath.includes('/transaction/validation-')) {
      return false;
    }
    
    // ENHANCED: Don't restore if this is a fresh tab
    if (this.isFreshTab()) {
      console.log('SessionStateManager: Fresh tab detected, not restoring session');
      return false;
    }
    
    // Check if session is recent (within last hour)
    if (sessionState.timestamp) {
      const sessionTime = new Date(sessionState.timestamp);
      const now = new Date();
      const hoursDiff = (now.getTime() - sessionTime.getTime()) / (1000 * 60 * 60);
      
      if (hoursDiff > 1) {
        console.log('SessionStateManager: Session too old, not restoring');
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check if this is a fresh tab (no sessionStorage data)
   * This indicates the tab was just opened or the previous tab was closed
   */
  isFreshTab(): boolean {
    try {
      // Check if sessionStorage has any session data
      const sessionState = sessionStorage.getItem(STORAGE_KEYS.SESSION_STATE);
      const stepperState = sessionStorage.getItem('clientInformationStepperState');
      
      // If no sessionStorage data exists, this is a fresh tab
      const isFresh = !sessionState && !stepperState;
      
      if (isFresh) {
        console.log('SessionStateManager: Fresh tab detected - no sessionStorage data found');
      }
      
      return isFresh;
    } catch (error) {
      console.error('SessionStateManager: Error checking fresh tab status:', error);
      return true; // Default to fresh tab on error
    }
  }

  /**
   * Check if current path is a root/landing path
   */
  isRootPath(): boolean {
    const currentPath = window.location.pathname;
    const rootPaths = [
      '/',
      '/travel-portal/',
      '/travel-portal/customer-portal',
      '/travel-portal/customer-portal/'
    ];
    
    return rootPaths.some(rootPath => currentPath === rootPath || currentPath.endsWith(rootPath));
  }

  /**
   * Restore session data for a specific section
   */
  restoreSessionForSection(section: CustomerPortalSection): Partial<SessionState> {
    const sessionState = this.getSessionState();
    
    console.log('SessionStateManager: Restoring session for section:', section, sessionState);
    
    switch (section) {
      case 'travel-information':
        return {
          activeSection: section,
          planSelection: sessionState.planSelection,
          travelFormData: sessionState.travelFormData
        };
        
      case 'travel-details':
        return {
          activeSection: section,
          planSelection: sessionState.planSelection,
          travelFormData: sessionState.travelFormData
        };
        
      case 'email-validation':
        return {
          activeSection: section,
          planSelection: sessionState.planSelection,
          travelFormData: sessionState.travelFormData,
          verificationEmail: sessionState.verificationEmail
        };
        
      case 'client-information':
        return {
          activeSection: section,
          planSelection: sessionState.planSelection,
          travelFormData: sessionState.travelFormData,
          clientInformationData: sessionState.clientInformationData,
          stepperState: sessionState.stepperState
        };
        
      default:
        return {
          activeSection: section
        };
    }
  }

  /**
   * Clear session state
   */
  clearSessionState(): void {
    try {
      // CRITICAL FIX: Enhanced check for active email verification to prevent token interference
      const urlParams = new URLSearchParams(window.location.search);
      const hasActiveEmailVerification = urlParams.has('id') && urlParams.has('token');
      
      // ADDITIONAL FIX: Check for recent verification activity
      const emailVerificationTimestamp = localStorage.getItem('emailVerificationTimestamp');
      const isRecentVerification = emailVerificationTimestamp &&
        (Date.now() - new Date(emailVerificationTimestamp).getTime()) < 60000; // 1 minute
      
      if (hasActiveEmailVerification || isRecentVerification) {
        console.log('SessionStateManager: Email verification in progress or recent, skipping session clear to prevent token interference', {
          hasActiveEmailVerification,
          isRecentVerification,
          timestamp: emailVerificationTimestamp
        });
        return;
      }
      
      sessionStorage.removeItem(STORAGE_KEYS.SESSION_STATE);
      localStorage.removeItem(STORAGE_KEYS.SESSION_STATE);
      
      // Also clear individual keys for backward compatibility
      Object.values(STORAGE_KEYS).forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });
      
      // CRITICAL FIX: Clear all related data that might cause conflicts
      const additionalKeysToRemove = [
        'emailVerificationComplete',
        'emailVerificationTempData',
        'emailVerificationPlanType',
        'emailVerificationShouldSetupClient',
        'emailVerificationTimestamp',
        'currentVerificationId',
        'planSelectionTempData',
        'clientInformationStepperState'
      ];
      
      additionalKeysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });
      
      console.log('SessionStateManager: Cleared session state and all related data');
    } catch (error) {
      console.error('SessionStateManager: Error clearing session state:', error);
    }
  }

  /**
   * Check if session has valid travel data
   */
  hasValidTravelData(): boolean {
    const sessionState = this.getSessionState();
    return !!(sessionState.planSelection && sessionState.travelFormData);
  }

  /**
   * Get plan type from URL for direct access scenarios
   */
  getPlanTypeFromUrl(): string | null {
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/').filter(segment => segment);
    
    const clientInfoIndex = pathSegments.indexOf('client-information');
    if (clientInfoIndex !== -1 && clientInfoIndex + 1 < pathSegments.length) {
      const planTypeSlug = pathSegments[clientInfoIndex + 1];
      return this.convertSlugToPlanType(planTypeSlug);
    }
    
    return null;
  }

  /**
   * Convert URL slug to plan type
   */
  private convertSlugToPlanType(slug: string): 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip' {
    const slugMap: Record<string, 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip'> = {
      'individual': 'Individual Plan',
      'family': 'Family Plan',
      'group': 'Group Plan',
      'multi-trip': 'Multi Trip'
    };
    return slugMap[slug] || 'Individual Plan';
  }

  /**
   * Create minimal data for direct URL access
   */
  createMinimalDataForPlanType(planType: string): { planSelection: PlanSelection; travelFormData: TravelFormData } {
    const planSelection: PlanSelection = {
      planType: planType as 'Individual Plan' | 'Family Plan' | 'Group Plan' | 'Multi Trip',
      section: 'Domestic' as const
    };
    
    const travelFormData: TravelFormData = {
      planType: planType === 'Individual Plan' ? 'Individual' :
               planType === 'Family Plan' ? 'Family' :
               planType === 'Group Plan' ? 'Group' : 'Multi Trip',
      tripType: planType === 'Multi Trip' ? 'Annual Multi-Trip' : 'Single Trip',
      startTrip: '',
      destination: [],
      endTrip: '',
      purposeOfTravel: '',
      startDate: '',
      endDate: '',
      travelDate: planType === 'Multi Trip' ? new Date().getFullYear().toString() : ''
    };
    
    return { planSelection, travelFormData };
  }

  /**
   * Check if current session is from email verification flow
   */
  isEmailVerificationFlow(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const hasEmailParams = urlParams.has('id') && urlParams.has('token');
    
    // Check for email verification flags in storage (sessionStorage first)
    const emailVerificationComplete = sessionStorage.getItem('emailVerificationComplete') ||
                                    localStorage.getItem('emailVerificationComplete');
    const emailVerificationTempData = sessionStorage.getItem('emailVerificationTempData') ||
                                    localStorage.getItem('emailVerificationTempData');
    
    return hasEmailParams || (emailVerificationComplete === 'true' && !!emailVerificationTempData);
  }

  /**
   * Check if there's an active email verification flow that should be protected
   * This is more specific than isEmailVerificationFlow and checks for active token processing
   */
  isActiveEmailVerificationFlow(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const hasActiveTokenParams = urlParams.has('id') && urlParams.has('token');
    
    if (hasActiveTokenParams) {
      console.log('SessionStateManager: Active email verification detected - URL has token parameters');
      return true;
    }
    
    // Check if verification is currently in progress (recent verification data)
    const emailVerificationComplete = sessionStorage.getItem('emailVerificationComplete');
    const emailVerificationTimestamp = localStorage.getItem('emailVerificationTimestamp');
    
    if (emailVerificationComplete === 'true' && emailVerificationTimestamp) {
      const verificationTime = new Date(emailVerificationTimestamp);
      const now = new Date();
      const minutesDiff = (now.getTime() - verificationTime.getTime()) / (1000 * 60);
      
      // Consider verification active if it happened within the last 5 minutes
      if (minutesDiff < 5) {
        console.log('SessionStateManager: Recent email verification detected - protecting verification data');
        return true;
      }
    }
    
    return false;
  }

  /**
   * Enhanced sync stepper state from localStorage to session state with coordination
   */
  syncStepperState(): void {
    try {
      // Use sessionStorage only for stepper state (clears when tab closes)
      const stepperState = sessionStorage.getItem('clientInformationStepperState');
      if (stepperState) {
        const parsed = JSON.parse(stepperState);
        console.log('SessionStateManager: Syncing stepper state:', parsed);
        
        // Validate stepper state integrity
        if (this.validateStepperState(parsed)) {
          // Update session state with stepper information
          const currentSessionState = this.getSessionState();
          this.saveSessionState({
            ...currentSessionState,
            stepperState: {
              currentStep: parsed.currentStep,
              stepperData: parsed.stepperData || {},
              planType: parsed.planType,
              coverageType: parsed.coverageType,
              showStepper: true,
              selectedOption: parsed.coverageType === 'myself' ? 'myself' : 'someone-else'
            },
            stepperStateVersion: parsed.timestamp || new Date().toISOString(),
            lastStepperUpdate: new Date().toISOString()
          });
          
          console.log('SessionStateManager: ✅ Stepper state synced successfully');
        } else {
          console.warn('SessionStateManager: ❌ Invalid stepper state, skipping sync');
        }
      }
    } catch (error) {
      console.error('SessionStateManager: Error syncing stepper state:', error);
    }
  }

  /**
   * Validate stepper state integrity
   */
  private validateStepperState(stepperState: unknown): boolean {
    if (!stepperState || typeof stepperState !== 'object' || stepperState === null) {
      return false;
    }
    
    const state = stepperState as Record<string, unknown>;
    
    // Check required fields
    const requiredFields = ['currentStep', 'planType', 'coverageType'];
    for (const field of requiredFields) {
      if (!state[field]) {
        console.warn(`SessionStateManager: Missing required field: ${field}`);
        return false;
      }
    }
    
    // Validate step name format
    const validSteps = [
      'applicant-details',
      'principal-insured',
      'home-address',
      'contact-information',
      'beneficiary-details',
      'family-information',
      'personal-accident-beneficiary',
      'group-members-information',
      'summary'
    ];
    
    if (typeof state.currentStep !== 'string' || !validSteps.includes(state.currentStep)) {
      console.warn(`SessionStateManager: Invalid step: ${state.currentStep}`);
      return false;
    }
    
    return true;
  }

  /**
   * Enhanced stepper state coordination - save stepper state to both localStorage and session
   */
  saveStepperState(stepperState: {
    currentStep: string;
    stepperData: Record<string, unknown>;
    planType: string;
    coverageType: string;
    showStepper: boolean;
    selectedOption: string;
  }): void {
    try {
      const timestamp = new Date().toISOString();
      
      // Save to sessionStorage only (clears when tab closes)
      const sessionStorageState = {
        ...stepperState,
        timestamp
      };
      sessionStorage.setItem('clientInformationStepperState', JSON.stringify(sessionStorageState));
      
      // Save to session state (for coordination)
      const currentSessionState = this.getSessionState();
      this.saveSessionState({
        ...currentSessionState,
        stepperState,
        stepperStateVersion: timestamp,
        lastStepperUpdate: timestamp
      });
      
      console.log('SessionStateManager: ✅ Coordinated stepper state saved:', {
        currentStep: stepperState.currentStep,
        planType: stepperState.planType,
        hasStepperData: Object.keys(stepperState.stepperData).length > 0
      });
    } catch (error) {
      console.error('SessionStateManager: Error saving coordinated stepper state:', error);
    }
  }

  /**
   * Get stepper state from session
   */
  getStepperState(): SessionState['stepperState'] | null {
    const sessionState = this.getSessionState();
    return sessionState.stepperState || null;
  }

  /**
   * Check if stepper state exists and is valid
   */
  hasValidStepperState(): boolean {
    const stepperState = this.getStepperState();
    return !!(stepperState && stepperState.currentStep && stepperState.planType);
  }

  /**
   * Clear stepper state from session
   */
  clearStepperState(): void {
    const currentSessionState = this.getSessionState();
    delete currentSessionState.stepperState;
    this.saveSessionState(currentSessionState);
    
    // Clear from sessionStorage only
    sessionStorage.removeItem('clientInformationStepperState');
  }
}

// Export singleton instance
export const sessionStateManager = SessionStateManager.getInstance();